import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { CircularProgress } from '@mui/material';
import { Check, CheckLine, Plus } from 'lucide-react';
import React, { ElementType, memo, useState } from 'react';
import useWidth from '../../../components/Hooks/useWidth';
import LangEnum from '../../../lang/LangEnum';
import { useCombinationButtonStatus } from '../../orders/products/product/grid/store/useCombinationButtonStatus';
import { useTranslation } from '../../translation/TranslationContext';
import AddOrderIconButton, { AddOrderIconButtonSize } from './components/AddOrderIconButton';
import AddedOrderProductDropdown from './components/AddedOrderProductDropdown/AddedOrderProductDropdown';
import { CombinationButtonStatusEnum, IGridStatus } from './hooks/useCombination';
import {
  getAddOrderButtonTooltipMessage,
  isItemInOrderStatus,
  isItemShownAsInOrder,
} from './hooks/useCombination.utils';

const NOT_A_TAB_STOP = -1;
const LOADING_SPINNER_SIZE = 16;

const AddOrderButtonSpinner = () => (
  <CircularProgress size={LOADING_SPINNER_SIZE} color="inherit" />
);

const BUTTON_ICON_BY_STATUS: Record<CombinationButtonStatusEnum, ElementType> = {
  [CombinationButtonStatusEnum.ADDED]: Check,
  [CombinationButtonStatusEnum.WITH_PIECES]: Check,
  [CombinationButtonStatusEnum.SET_WINDOW]: CheckLine,
  [CombinationButtonStatusEnum.ADD]: Plus,
  [CombinationButtonStatusEnum.LOADING]: AddOrderButtonSpinner,
};

export interface ProductDetailsAddOrderButtonV2Props {
  disabled: boolean;
  gridButtonStatus: IGridStatus;
  combinationKey?: string;
  hasEmptyAvailability?: boolean;
  removeActionCustomLabel?: LangEnum;
  tooltipMessage?: LangEnum;
  testId?: string;
  onAdd?: () => void;
  onRemove?: () => void;
}

const ProductDetailsAddOrderButtonV2 = ({
  disabled,
  gridButtonStatus,
  combinationKey,
  hasEmptyAvailability,
  removeActionCustomLabel,
  tooltipMessage,
  testId,
  onAdd,
  onRemove,
}: ProductDetailsAddOrderButtonV2Props) => {
  const status = useCombinationButtonStatus({
    combinationKey,
    hasEmptyAvailability: !!hasEmptyAvailability,
    fallback: gridButtonStatus,
  });
  const { mediumDownWidth } = useWidth();
  const { translate } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isDropdownOpen = Boolean(anchorEl);

  const itemIsInOrder = isItemInOrderStatus(status);
  const isShownAsInOrder = isItemShownAsInOrder(status);
  const resolvedTooltipMessage = getAddOrderButtonTooltipMessage({ status, tooltipMessage });

  const handleConfirmRemoveOrderProduct = () => {
    onRemove?.();
    handleCloseDropdown();
  };

  const handleClickDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
  };

  const handleActionAddOrderButton = (event: React.MouseEvent<HTMLElement>) => {
    if (!itemIsInOrder) {
      onAdd?.();
      return;
    }

    handleClickDropdown(event);
  };

  const size: AddOrderIconButtonSize = mediumDownWidth ? 'large' : 'medium';

  return (
    <>
      <Tooltip
        tabIndex={NOT_A_TAB_STOP}
        TooltipContent={resolvedTooltipMessage ? translate(resolvedTooltipMessage) : undefined}
      >
        <AddOrderIconButton
          size={size}
          testId={testId}
          tabIndex={NOT_A_TAB_STOP}
          isInOrder={itemIsInOrder}
          isShownAsInOrder={isShownAsInOrder}
          icon={BUTTON_ICON_BY_STATUS[status.type]}
          disabled={disabled || status.type === CombinationButtonStatusEnum.LOADING}
          onClick={handleActionAddOrderButton}
        />
      </Tooltip>
      <AddedOrderProductDropdown
        open={isDropdownOpen}
        anchorEl={anchorEl}
        handleClose={handleCloseDropdown}
        onClickRemove={() => handleConfirmRemoveOrderProduct()}
        removeActionCustomLabel={removeActionCustomLabel}
      />
    </>
  );
};

export default memo(ProductDetailsAddOrderButtonV2);
